import "crypto-js/lib-typedarrays";

import { CredentialResponse, GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { decodeToken } from "react-jwt";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import ErrorToast from "../../../components/ErrorToast";
import PasswordInput from "../../../components/PasswordInput";
import Spinner from "../../../components/Spinner";
import { useAuth } from "../../../context/Auth/AuthContext";
import { StaticValue } from "../../../seed/constants";
import { hasUserRecord } from "../../../services/auth/accountRecovery";
import { userPool } from "../../../services/auth/cognito";
import ResendVerificationButton from "../components/ResendVerificationButton";
import { clearPendingVerificationEmail, SignupMethod, setPendingSignup } from "../pendingSignup";
import LinkedInLogin from "./LinkedInLogin";

const staticValue = new StaticValue();
const staticValues = staticValue.getDefaultValues();

const HOME_PAGES = ["/", "/signin"];
const PROFILE = "/profile";

const referrer = document.referrer ? new URL(document.referrer) : null;
const goToOptions = referrer ? `${referrer.pathname}${referrer.search}` : PROFILE;
const goTo = HOME_PAGES.includes(goToOptions) ? PROFILE : goToOptions;

const referer = new URLSearchParams(window.location.search).get("ref");
const redirectTo = referer ? referer : goTo;

function isAndroidPhone() {
	const userAgent = window.navigator.userAgent.match(/Android/i);
	return userAgent !== null;
}

interface IdTokenPayload {
	email: string;
	given_name: string;
	family_name: string;
	picture: string;
}

/**
 * Sign-in succeeded, but the identity may have no backend user document (see accountRecovery).
 * Those users have to finish onboarding first — sending them to their profile instead means every
 * authenticated call 401s and the response interceptor bounces them straight back to this form with
 * no explanation of what went wrong.
 */
async function goToDestination(profile: IdTokenPayload, method: SignupMethod, token: string) {
	if (!(await hasUserRecord())) {
		// Cognito only ever stores the email attribute, so given_name/family_name are undefined for
		// email accounts. Onboarding asks for the name when the draft doesn't carry one.
		setPendingSignup({
			firstName: profile.given_name || "",
			lastName: profile.family_name || "",
			email: profile.email,
			method,
			emailVerified: true,
		});
		window.location.href = "/onboarding";
		return;
	}

	if (isAndroidPhone()) {
		try {
			window.location.href = `dproz://redirect?token=${encodeURIComponent(token)}`;
			return;
		} catch {
			// No handler registered for the scheme — fall through to the web destination.
		}
	}

	window.location.href = redirectTo;
}

function SigninForm() {
	const checkboxRef = React.createRef<HTMLInputElement>();

	const [authData, setAuthData] = useState({
		email: "",
		password: "",
		remember_me: false,
	});

	const { setUserData } = useAuth();

	useEffect(() => {
		const remember_me = JSON.parse(localStorage.getItem("remember_me"));

		if (remember_me) {
			setAuthData((prev) => ({
				...prev,
				email: remember_me.email,
				remember_me: remember_me.remember_me,
			}));
		}
	}, []);

	const { email, password, remember_me } = authData;
	const [_, setCookie, removeCookie] = useCookies<string>(["Authorization"]);
	const [error, setError] = useState({ email: "", password: "", serverErrors: "" });
	// Set when Cognito reports the account exists but the email link was never followed.
	const [unconfirmedEmail, setUnconfirmedEmail] = useState("");

	function onChange(event: React.ChangeEvent<HTMLInputElement>) {
		const { name, type, value, checked } = event.target;
		setAuthData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	}
	const [loading, setLoading] = useState(false);
	useEffect(() => {}, [loading]);

	function handleSubmit(event: React.FormEvent | React.MouseEvent) {
		event.preventDefault();
		setError({
			email: "",
			password: "",
			serverErrors: "",
		});
		setUnconfirmedEmail("");
		setLoading(true);

		if (email.length === 0) {
			setError((prev) => ({ ...prev, email: "Email is required" }));
			setLoading(false);
			return;
		}

		if (password.length === 0) {
			setError((prev) => ({ ...prev, password: "Password is required" }));
			setLoading(false);
			return;
		}

		if (remember_me) {
			localStorage.setItem("remember_me", JSON.stringify({ email, remember_me }));
		} else {
			localStorage.removeItem("remember_me");
		}

		let authenticationData = {
			Username: email,
			Password: password,
		};

		let authenticationDetails = new AuthenticationDetails(authenticationData);

		let userData = {
			Username: email,
			Pool: userPool,
		};

		let cognitoUser = new CognitoUser(userData);

		cognitoUser.authenticateUser(authenticationDetails, {
			onSuccess: function (result) {
				let idToken = `CO::${result.getIdToken().getJwtToken()}`;
				const expireDate = new Date();
				expireDate.setDate(expireDate.getDate() + 30);
				setCookie("Authorization", idToken, {
					path: "/",
					expires: expireDate,
				});

				// They made it in — stop showing the "check your email" screen at /creating-profile.
				clearPendingVerificationEmail();

				let cognitoUser = userPool.getCurrentUser();

				if (cognitoUser != null) {
					cognitoUser.getSession(function (err, result) {
						if (result) {
							removeCookie(staticValues.localStorageResumeId);
							localStorage.removeItem("resumeList");
							setLoading(false);
							const decodedToken = decodeToken<IdTokenPayload>(result.getIdToken().getJwtToken());

							const data = {
								email: decodedToken.email,
								lastName: decodedToken.family_name,
								firstName: decodedToken.given_name,
								picture: decodedToken.picture,
							};

							localStorage.setItem("profileData", JSON.stringify(data));
							setUserData(data);

							goToDestination(decodedToken, "email", idToken);
						}
					});
				}
			},
			onFailure: function (err) {
				const code = err?.code || err?.name;

				if (code === "UserNotConfirmedException") {
					// The account exists, the password was right — they just never followed the emailed
					// activation link. Setting this renders the resend button below the message.
					setUnconfirmedEmail(email);
					setError((prev) => ({
						...prev,
						serverErrors:
							"You haven't verified your account yet. Follow the activation link we emailed you to finish signing in.",
					}));
				} else if (code === "NotAuthorizedException") {
					setError((prev) => ({ ...prev, serverErrors: "Incorrect email or password" }));
				} else if (code === "UserNotFoundException") {
					setError((prev) => ({ ...prev, serverErrors: "We couldn't find an account with that email address." }));
				} else if (code === "PasswordResetRequiredException") {
					setError((prev) => ({
						...prev,
						serverErrors: "Your password needs to be reset — use “I forgot password” below.",
					}));
				} else {
					// Don't blame verification for errors we haven't identified — that used to send verified
					// users off to hunt for an activation email that had nothing to do with the failure.
					setError((prev) => ({
						...prev,
						serverErrors: err?.message || "We couldn't sign you in. Please try again.",
					}));
				}
				setLoading(false);
			},
		});
	}

	useEffect(() => {}, [error]);

	function onGoogleSignIn(googleUser: CredentialResponse) {
		if (googleUser.credential) {
			const credential = `GO::${googleUser.credential}`;

			const expireDate = new Date();
			expireDate.setDate(expireDate.getDate() + 30);
			setCookie("Authorization", credential, {
				path: "/",
				expires: expireDate,
			});

			removeCookie(staticValues.localStorageResumeId);
			localStorage.removeItem("resumeList");
			setLoading(false);
			const decodedToken = decodeToken<IdTokenPayload>(googleUser.credential);

			const data = {
				...decodedToken,
				email: decodedToken.email,
				firstName: decodedToken.given_name,
				lastName: decodedToken.family_name,
				picture: decodedToken.picture,
			};

			localStorage.setItem("profileData", JSON.stringify(data));
			setUserData(data);

			goToDestination(decodedToken, "google", credential);
		}
	}

	const [showEmailOptions, setShowEmailOptions] = useState(false);

	const profileData = localStorage.getItem("profileData");

	useGoogleOneTapLogin({
		onSuccess: onGoogleSignIn,
		auto_select: !!profileData,
	});

	const buttonRef = useRef<HTMLDivElement>(null);

	const buttonWidth = useMemo(() => {
		if (buttonRef.current) {
			return buttonRef.current.offsetWidth;
		}
		return 0;
	}, [buttonRef.current]);

	return (
		<>
			<div className="mb-4 grid md:grid-cols-2 gap-4">
				<LinkedInLogin />
				<div className="flex items-center justify-center" ref={buttonRef}>
					<GoogleLogin
						onSuccess={onGoogleSignIn}
						onError={() => toast.error("Login Failed")}
						size="large"
						width={`${buttonWidth}px`}
						text="continue_with"
					/>
				</div>
			</div>
			<div className="mb-3 text-center">
				<p className="text-medium font-medium">OR</p>
			</div>
			<div className="">{error.serverErrors !== "" && <ErrorToast error={error.serverErrors} />}</div>
			{unconfirmedEmail && (
				<ResendVerificationButton email={unconfirmedEmail} source="signin" className="mb-4 text-center" />
			)}
			{showEmailOptions ? (
				<form onSubmit={handleSubmit} className="tr-form">
					<div className="flex flex-col gap-1 mb-4">
						<input
							type="email"
							className="border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
							placeholder="Enter your email address"
							value={email}
							onChange={onChange}
							name="email"
						/>
						{error.email && <p className="text-sm text-error-default">{error.email}</p>}
					</div>
					<div className="flex flex-col gap-1 mb-4">
						<PasswordInput
							className="border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
							placeholder="Enter your password"
							onChange={onChange}
							value={password}
							name="password"
						/>
						{error.password && <p className="text-sm text-error-default">{error.password}</p>}
					</div>
					<div className="flex justify-between mb-4">
						<label className="flex items-center p-0" htmlFor="legalNameToSAcknowledged">
							<input type="checkbox" ref={checkboxRef} name="remember_me" checked={remember_me} onChange={onChange} />
							<span
								className="checkmark"
								onClick={() => {
									checkboxRef.current.click();
								}}
							/>
							<div className="pl-2">
								<p className="text-base font-medium m-0 text-black">Remember me</p>
							</div>
						</label>
						<div className="forgot-password">
							<Link to="/forgot-password" className="font-medium">
								I forgot password
							</Link>
						</div>
					</div>

					<button
						className="w-full rounded-md bg-primary-700 text-white font-inter text-base py-2 flex items-center justify-center"
						onClick={handleSubmit}
					>
						{loading ? <Spinner /> : "Login"}
					</button>
				</form>
			) : (
				<>
					<button
						className="w-full rounded-md hover:bg-gray-100 border border-gray-500 text-gray-700 font-inter text-base py-2 flex items-center justify-center"
						onClick={() => setShowEmailOptions(true)}
					>
						Continue with Email
					</button>
				</>
			)}
			<div className="text-sm text-[#5C5C5C] mt-4">
				<span>
					Can&#39;t log in?{" "}
					<Link to={"/register?ref=" + window.location.pathname} className="font-semibold text-primary-700">
						Create a New Account
					</Link>
				</span>
			</div>
		</>
	);
}

export default SigninForm;
